/* eslint-disable no-unused-vars */
import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "bootstrap-icons/font/bootstrap-icons.css";

import {
  fetchEmployeePersonalDetailsById,
  createEmployeePersonalDetails,
  updateEmployeePersonalDetails,
  clearEmployee,
  checkEmployeeCompleteness,
} from "../../store/slices/employeePersonalDetailSlice";
import { fetchEmployeeById } from "../../store/slices/employeeDetailSlice";

const EmployeePersonalDetails = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const { id: employeeId } = useParams();
  const { employee, status } = useSelector(
    (state) => state.employeePersonalDetail,
  );

  const [isEditing, setIsEditing] = useState(true); // Default to true based on requirements
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [statusMsg, setStatusMsg] = useState({ text: "", type: "" });

  const [formData, setFormData] = useState({
    gender: "Other",
    civilStatus: "Single",
    religion: "",
    height: "",
    weight: "",
    birthDate: "",
    birthPlace: "",
    mothersMaidenName: "",
    contactNumber: "",
    alternateContactNumber: "",
  });

  const religionOptions = [
    "Roman Catholic",
    "Islam",
    "Iglesia ni Cristo",
    "Christian Bible Church",
    "Seventh-day Adventist",
    "Jehovah’s Witnesses",
    "Baptist",
    "Pentecostal",
    "Anglican / Episcopal",
    "LDS (Mormon)",
    "Buddhism",
    "Hinduism",
    "Sikhism",
    "Judaism",
    "Atheist",
    "Agnostic",
    "Others",
    "Prefer not to say",
  ];

  const calculatedAge = useMemo(() => {
    if (!formData.birthDate) return "";
    const today = new Date();
    const birthDate = new Date(formData.birthDate);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age >= 0 ? age : "";
  }, [formData.birthDate]);

  useEffect(() => {
    if (employeeId) dispatch(fetchEmployeePersonalDetailsById(employeeId));
    return () => dispatch(clearEmployee());
  }, [employeeId, dispatch]);

  useEffect(() => {
    if (employee) {
      const formattedBirthDate = employee.birthDate
        ? employee.birthDate.split("T")[0]
        : "";

      setFormData({
        gender: employee.gender ?? "Other",
        civilStatus: employee.civilStatus ?? "Single",
        religion: employee.religion ?? "",
        height: employee.height ?? "",
        weight: employee.weight ?? "",
        birthDate: formattedBirthDate,
        birthPlace: employee.birthPlace ?? "",
        mothersMaidenName: employee.mothersMaidenName ?? "",
        contactNumber: employee.contactNumber ?? "",
        alternateContactNumber:
          employee.alternateContactNumber ?? employee.alternateContactNo ?? "",
      });
      setIsEditing(false);
    }
  }, [employee]);

  useImperativeHandle(ref, () => ({
    submit: async () => {
      return await handleSubmit();
    },
  }));

  const validate = () => {
    let newErrors = {};
    if (!formData.gender || formData.gender === "Other")
      newErrors.gender = true;
    if (!formData.mothersMaidenName?.trim()) newErrors.mothersMaidenName = true;

    if (!formData.contactNumber || formData.contactNumber.trim() === "") {
      newErrors.contactNumber = true;
    } else if (!isValidPhoneNumber(formData.contactNumber)) {
      newErrors.contactNumberInvalid = true;
    }

    if (
      formData.alternateContactNumber &&
      formData.alternateContactNumber.trim() !== ""
    ) {
      if (!isValidPhoneNumber(formData.alternateContactNumber)) {
        newErrors.alternateContactNumberInvalid = true;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: false }));
    if (statusMsg.text) setStatusMsg({ text: "", type: "" });
  };

  const handlePhoneChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value || "" }));
    if (name === "contactNumber") {
      setErrors((prev) => ({
        ...prev,
        contactNumber: false,
        contactNumberInvalid: false,
      }));
    }
    if (name === "alternateContactNumber") {
      setErrors((prev) => ({ ...prev, alternateContactNumberInvalid: false }));
    }
    if (statusMsg.text) setStatusMsg({ text: "", type: "" });
  };

  const handleCancel = () => {
    if (employee) {
      const formattedBirthDate = employee.birthDate
        ? employee.birthDate.split("T")[0]
        : "";

      setFormData({
        gender: employee.gender ?? "Other",
        civilStatus: employee.civilStatus ?? "Single",
        religion: employee.religion ?? "",
        height: employee.height ?? "",
        weight: employee.weight ?? "",
        birthDate: formattedBirthDate,
        birthPlace: employee.birthPlace ?? "",
        mothersMaidenName: employee.mothersMaidenName ?? "",
        contactNumber: employee.contactNumber ?? "",
        alternateContactNumber:
          employee.alternateContactNumber ?? employee.alternateContactNo ?? "",
      });
    }
    setIsEditing(false);
    setErrors({});
    setStatusMsg({ text: "", type: "" });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) {
      setStatusMsg({ text: "Missing required fields.", type: "danger" });
      return;
    }

    setIsSaving(true);
    setStatusMsg({ text: "", type: "" });

    try {
      const payload = {
        ...formData,
        civilStatus: formData.civilStatus,
        birthDate: formData.birthDate === "" ? null : formData.birthDate,
        employeeId,
        alternateContactNo: formData.alternateContactNumber,
      };

      if (employee?.id) {
        await dispatch(
          updateEmployeePersonalDetails({ id: employee.id, data: payload }),
        ).unwrap();
      } else {
        await dispatch(createEmployeePersonalDetails(payload)).unwrap();
      }

      await dispatch(checkEmployeeCompleteness(employeeId)).unwrap();

      setStatusMsg({ text: "Saved successfully!", type: "success" });
      setIsEditing(false);
      dispatch(fetchEmployeePersonalDetailsById(employeeId));
      dispatch(fetchEmployeeById(employeeId));

      setTimeout(() => setStatusMsg({ text: "", type: "" }), 4000);
    } catch (err) {
      const errorMessage =
        typeof err === "string" ? err : err?.message || "Failed to save.";
      setStatusMsg({ text: errorMessage, type: "danger" });
    } finally {
      setIsSaving(false);
    }
  };

  const inputStyle = (name) => ({
    height: "32px",
    fontSize: "0.85rem",
    borderRadius: "8px",
    border: !isEditing
      ? "none"
      : errors[name]
        ? "1.5px solid #dc3545"
        : "1px solid rgba(0,0,0,0.1)",
    backgroundColor: !isEditing ? "#f8fafc" : "#ffffff",
  });

  return (
    <div className="container-fluid py-2 px-0">
      <style>{`
        .extra-small { font-size: 0.7rem; letter-spacing: 0.02em; }
        .PhoneInput { 
            display: flex; 
            align-items: center; 
            padding: 0 10px; 
            height: 32px; 
            border-radius: 8px;
            background-color: ${!isEditing ? "#f8fafc" : "#ffffff"};
        }
        .phone-main { border: ${!isEditing ? "none" : errors.contactNumber || errors.contactNumberInvalid ? "1.5px solid #dc3545" : "1px solid rgba(0,0,0,0.1)"}; }
        .phone-alt { border: ${!isEditing ? "none" : errors.alternateContactNumberInvalid ? "1.5px solid #dc3545" : "1px solid rgba(0,0,0,0.1)"}; }
        .PhoneInputInput { flex: 1; border: none !important; font-size: 0.85rem !important; outline: none !important; background: transparent; }
        .PhoneInputCountryIcon--disabled, .PhoneInputCountrySelectIcon--disabled, .PhoneInputCountryIconImg { opacity: 1 !important; }
        .progress-overlay { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: rgba(255,255,255,0.7); z-index: 100; display: flex; align-items: center; justify-content: center; border-radius: 8px; }
      `}</style>

      <div className="bg-white p-3 rounded-3 shadow-sm border mb-4 position-relative">
        {isSaving && (
          <div className="progress-overlay">
            <div className="spinner-border text-primary"></div>
          </div>
        )}

        <div
          className="d-flex justify-content-between align-items-center px-3 py-2 rounded-2 mb-4"
          style={{ backgroundColor: "#1e293b", minHeight: "44px" }}
        >
          <div className="d-flex align-items-center"></div>

          <div className="d-flex align-items-center gap-3">
            {!isEditing ? (
              <div className="d-flex align-items-center gap-3">
                {statusMsg.text && (
                  <span
                    className={`fw-bold text-uppercase d-flex align-items-center text-${statusMsg.type}`}
                    style={{ fontSize: "0.65rem", letterSpacing: "0.05em" }}
                  >
                    <i
                      className={`bi ${statusMsg.type === "success" ? "bi-check-circle-fill" : "bi-exclamation-triangle-fill"} me-2`}
                    ></i>
                    {statusMsg.text}
                  </span>
                )}
                <button
                  type="button"
                  className="btn btn-sm py-0 px-2 shadow-sm d-flex align-items-center justify-content-center"
                  style={{
                    backgroundColor: "#0f172a",
                    border: "none",
                    width: "32px",
                    height: "28px",
                  }}
                  onClick={() => setIsEditing(true)}
                >
                  <i className="bi bi-pencil-square text-white"></i>
                </button>
              </div>
            ) : (
              <div className="d-flex align-items-center gap-3">
                {statusMsg.text && (
                  <span
                    className={`fw-bold text-uppercase d-flex align-items-center text-${statusMsg.type}`}
                    style={{ fontSize: "0.65rem", letterSpacing: "0.05em" }}
                  >
                    <i
                      className={`bi ${statusMsg.type === "success" ? "bi-check-circle-fill" : "bi-exclamation-triangle-fill"} me-2`}
                    ></i>
                    {statusMsg.text}
                  </span>
                )}
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-success btn-sm py-0 px-2 shadow-sm"
                    style={{ width: "32px", height: "28px" }}
                    onClick={handleSubmit}
                    disabled={isSaving}
                  >
                    <i className="bi bi-check-lg text-white"></i>
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm py-0 px-2 shadow-sm"
                    style={{
                      backgroundColor: "#64748b",
                      border: "none",
                      width: "32px",
                      height: "28px",
                    }}
                    onClick={handleCancel}
                  >
                    <i className="bi bi-x-lg text-white"></i>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="row g-3 px-2">
            <div className="col-md-4">
              <label className="fw-semibold extra-small mb-1 text-muted text-uppercase">
                Gender <span className="text-danger">*</span>
              </label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                disabled={!isEditing}
                className={`form-select form-select-sm shadow-none ${errors.gender ? "border-danger" : ""}`}
                style={inputStyle("gender")}
              >
                <option value="Other">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
            <div className="col-md-4">
              <label className="fw-semibold extra-small mb-1 text-muted text-uppercase">
                Civil Status
              </label>
              <select
                name="civilStatus"
                value={formData.civilStatus}
                onChange={handleChange}
                disabled={!isEditing}
                className="form-select form-select-sm shadow-none"
                style={inputStyle("civilStatus")}
              >
                <option value="Single">Single</option>
                <option value="Married">Married</option>
                <option value="Widowed">Widowed</option>
                <option value="Divorced">Divorced</option>
                <option value="Separated"> Separated</option>
              </select>
            </div>
            <div className="col-md-4">
              <label className="fw-semibold extra-small mb-1 text-muted text-uppercase">
                Religion
              </label>
              <select
                name="religion"
                value={formData.religion}
                onChange={handleChange}
                disabled={!isEditing}
                className="form-select form-select-sm shadow-none"
                style={inputStyle("religion")}
              >
                <option value="">Select Religion</option>
                {religionOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="fw-semibold extra-small mb-1 text-muted text-uppercase">
                Birth Date
              </label>
              <input
                type="date"
                name="birthDate"
                value={formData.birthDate}
                onChange={handleChange}
                disabled={!isEditing}
                className="form-control form-control-sm shadow-none"
                style={inputStyle("birthDate")}
              />
            </div>
            <div className="col-md-1 text-center">
              <label className="fw-semibold extra-small mb-1 text-muted text-uppercase">
                Age
              </label>
              <div
                className="form-control form-control-sm text-center fw-bold"
                style={{
                  ...inputStyle(),
                  backgroundColor: "#e9ecef",
                  border: "none",
                }}
              >
                {calculatedAge || "--"}
              </div>
            </div>
            <div className="col-md-4">
              <label className="fw-semibold extra-small mb-1 text-muted text-uppercase">
                Birth Place
              </label>
              <input
                type="text"
                name="birthPlace"
                value={formData.birthPlace}
                onChange={handleChange}
                disabled={!isEditing}
                className="form-control form-control-sm shadow-none"
                style={inputStyle("birthPlace")}
                placeholder="City/Province"
              />
            </div>
            <div className="col-md-4">
              <label className="fw-semibold extra-small mb-1 text-muted text-uppercase">
                Mother's Maiden Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                name="mothersMaidenName"
                value={formData.mothersMaidenName}
                onChange={handleChange}
                disabled={!isEditing}
                className={`form-control form-control-sm shadow-none ${errors.mothersMaidenName ? "border-danger" : ""}`}
                style={inputStyle("mothersMaidenName")}
                placeholder="Maiden Name"
              />
            </div>

            <div className="col-md-6">
              <label className="fw-semibold extra-small mb-1 text-muted text-uppercase">
                Height (cm)
              </label>
              <input
                type="number"
                name="height"
                value={formData.height}
                onChange={handleChange}
                disabled={!isEditing}
                className="form-control form-control-sm shadow-none"
                style={inputStyle("height")}
                placeholder="e.g. 175"
              />
            </div>
            <div className="col-md-6">
              <label className="fw-semibold extra-small mb-1 text-muted text-uppercase">
                Weight (kg)
              </label>
              <input
                type="number"
                name="weight"
                value={formData.weight}
                onChange={handleChange}
                disabled={!isEditing}
                className="form-control form-control-sm shadow-none"
                style={inputStyle("weight")}
                placeholder="e.g. 70"
              />
            </div>

            <div className="col-md-6">
              <label className="fw-semibold extra-small mb-1 text-muted text-uppercase">
                Main Contact Number <span className="text-danger">*</span>
              </label>
              <PhoneInput
                className="PhoneInput phone-main"
                placeholder="Main contact"
                value={formData.contactNumber}
                onChange={(val) => handlePhoneChange("contactNumber", val)}
                disabled={!isEditing}
                defaultCountry="PH"
                international
              />
              {errors.contactNumberInvalid && (
                <span className="text-danger extra-small">
                  Invalid phone number format.
                </span>
              )}
            </div>
            <div className="col-md-6">
              <label className="fw-semibold extra-small mb-1 text-muted text-uppercase">
                Alternate Contact (Optional)
              </label>
              <PhoneInput
                className="PhoneInput phone-alt"
                placeholder="Optional"
                value={formData.alternateContactNumber}
                onChange={(val) =>
                  handlePhoneChange("alternateContactNumber", val)
                }
                disabled={!isEditing}
                defaultCountry="PH"
                international
              />
              {errors.alternateContactNumberInvalid && (
                <span className="text-danger extra-small">
                  Invalid phone number format.
                </span>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
});

export default EmployeePersonalDetails;